/**
 * Performance Monitoring with Web Vitals
 * Tracks Core Web Vitals and reports to analytics
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { addBreadcrumb, captureError } from './sentry';

// Performance thresholds (milliseconds)
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },    // Largest Contentful Paint
  CLS: { good: 0.1, poor: 0.25 },     // Cumulative Layout Shift
  FCP: { good: 1800, poor: 3000 },    // First Contentful Paint
  INP: { good: 200, poor: 500 },      // Interaction to Next Paint
  TTFB: { good: 800, poor: 1800 },    // Time to First Byte
};

type MetricName = keyof typeof THRESHOLDS;

function getRating(name: MetricName, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

function reportMetric(metric: Metric) {
  const rating = getRating(metric.name as MetricName, metric.value);

  // Add breadcrumb for debugging
  addBreadcrumb(`Web Vital: ${metric.name}`, 'performance', {
    value: metric.value,
    rating,
    delta: metric.delta,
    id: metric.id,
  });

  // Log poor metrics
  if (rating === 'poor') {
    console.warn(`Poor ${metric.name}:`, metric.value, metric);
  }

  // Send to analytics endpoint (if configured)
  if (import.meta.env.VITE_ANALYTICS_ENDPOINT) {
    const body = JSON.stringify({
      name: metric.name,
      value: metric.value,
      rating,
      delta: metric.delta,
      id: metric.id,
      page: window.location.pathname,
      timestamp: Date.now(),
    });

    // Use sendBeacon for reliability
    if (navigator.sendBeacon) {
      navigator.sendBeacon(import.meta.env.VITE_ANALYTICS_ENDPOINT, body);
    } else {
      fetch(import.meta.env.VITE_ANALYTICS_ENDPOINT, {
        method: 'POST',
        body,
        keepalive: true,
      }).catch(() => {
        // Ignore analytics failures
      });
    }
  }

  // Also report to Sentry for correlation with errors
  if (rating === 'poor') {
    captureError(
      new Error(`Poor Web Vital: ${metric.name} = ${metric.value.toFixed(2)}`),
      { metric: metric.name, value: metric.value, rating }
    );
  }
}

/**
 * Initialize performance monitoring
 * Call this once when the app loads
 */
export function initPerformanceMonitoring() {
  // Only run in browser
  if (typeof window === 'undefined') return;

  try {
    // Core Web Vitals (LCP, CLS, INP)
    onLCP(reportMetric);
    onCLS(reportMetric);
    onINP(reportMetric);

    // Additional metrics
    onFCP(reportMetric);
    onTTFB(reportMetric);

    console.log('[Performance] Web Vitals monitoring initialized');
  } catch (error) {
    console.error('[Performance] Failed to initialize:', error);
  }
}

/**
 * Measure a custom performance metric
 */
export function measureCustomMetric(name: string, startMark: string, endMark?: string) {
  if (typeof window === 'undefined' || !window.performance) return;

  try {
    if (endMark) {
      performance.measure(name, startMark, endMark);
    } else {
      performance.measure(name, startMark);
    }

    const entries = performance.getEntriesByName(name, 'measure');
    const lastEntry = entries[entries.length - 1];

    if (lastEntry) {
      addBreadcrumb(`Custom metric: ${name}`, 'performance', {
        duration: lastEntry.duration,
      });
    }
  } catch (error) {
    // Ignore measurement errors
  }
}

/**
 * Mark a performance point
 */
export function markPerformance(name: string) {
  if (typeof window === 'undefined' || !window.performance) return;

  try {
    performance.mark(name);
  } catch {
    // Ignore
  }
}
